import { skipToken } from '@reduxjs/toolkit/query'
import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { Avatar } from '../../../components/avatar'
import { Button } from '../../../components/button'
import CountCard from '../../../components/cards/CountCard'
import Icon from '../../../components/icon'
import { DateFilterNav } from '../../../components/inputs/dateFilterNav'
import Dropdown from '../../../components/inputs/dropdown'
import { SearchInput } from '../../../components/inputs/searchInput'
import { AddAmbassadorModal } from '../../../components/modal/app_modals/ambassadors/addAmbassadorModal'
import { ConfirmationModal } from '../../../components/modal/app_modals/confirmationModal'
import { TableComponent } from '../../../components/table'
import { CountCardContainer } from '../../../components/wrapper/CountCardsContainer'
import { formatCurrency, formatNumber } from '../../../helpers'
import { useModalControl } from '../../../hooks/useModalControl'
import {
  useDeleteAmbassadorMutation,
  useGetAmbassadorsMetricsQuery,
  useGetAmbassadorsQuery,
} from '../../../redux-toolkits/loyalty/loyalty.slice'
import { ReportPeriodRange } from '../../../redux-toolkits/reports/reports.type'
import { QueryProps } from '../../../redux-toolkits/shared/shared.type'
export const Ambassadors = () => {
  const [query, setQuery] = useState<QueryProps>()
  const [selectedAmbassadorId, setSelectedAmbassadorId] = useState<string>()

  const [searchParams, setSearchParams] = useSearchParams()

  const { modal, modalOpen, setModalOpen, handleModalClose, handleModalOpen } =
    useModalControl()

  const {
    data: ambassadors,
    isFetching: fetchingAmbassadors,
    isError,
  } = useGetAmbassadorsQuery(
    query
      ? {
          ...query,
        }
      : skipToken,
    {
      refetchOnMountOrArgChange: true,
    },
  )

  const {
    data: ambassadorsMetrics,
    isFetching: fetchingAmbassadorsMetrics,
    refetch: refetchAmbassadorsMetrics,
  } = useGetAmbassadorsMetricsQuery(
    query
      ? {
          startDate: query.startDate,
          endDate: query.endDate,
          filter: query.filter,
          isCustomDateRange: query.isCustomDateRange,
        }
      : skipToken,
    {
      refetchOnMountOrArgChange: true,
    },
  )

  const [deleteAffiliate, { isLoading: deletingAffiliate }] =
    useDeleteAmbassadorMutation()

  useEffect(() => {
    const custDate = searchParams.get('isCustomDateRange')
      ? searchParams.get('isCustomDateRange') === 'true'
      : false

    setQuery({
      page: Number(searchParams.get('page') || 1),
      limit: 20,
      search: searchParams.get('search') || undefined,
      startDate: custDate
        ? searchParams.get('startDate') || undefined
        : undefined,
      endDate: custDate ? searchParams.get('endDate') || undefined : undefined,
      isCustomDateRange: custDate,
      filter: custDate
        ? undefined
        : (searchParams.get('filter') as ReportPeriodRange) ||
          'inception_to_date',
    })
  }, [searchParams])

  return (
    <>
      <div className="flex flex-col gap-10">
        <DateFilterNav
          filter={query?.filter ?? 'custom'}
          isCustomDateRange={query?.isCustomDateRange ?? false}
          startDate={query?.startDate}
          endDate={query?.endDate}
          setFilter={(filter) => {
            setSearchParams({
              ...Object.fromEntries(searchParams),
              filter: filter.filter ?? '',
              isCustomDateRange: String(filter.isCustomDateRange),
              endDate: filter.endDate ?? '',
              startDate: filter.startDate ?? '',
              skip: '0',
            })
          }}
        />

        <CountCardContainer className="!my-0">
          <CountCard
            count={ambassadorsMetrics?.data?.totalAmbassadors ?? 0}
            text="Total ambassadors"
            isCurrency={false}
            loading={fetchingAmbassadorsMetrics}
            valueSuffix={
              (ambassadors?.data?.total ?? 0) === 1
                ? 'Ambassador'
                : 'Ambassadors'
            }
          />
          <CountCard
            count={ambassadorsMetrics?.data?.totalRevenueGenerated ?? 0}
            text="Total revenue generated"
            isCurrency={true}
            loading={fetchingAmbassadorsMetrics}
          />
          <CountCard
            count={ambassadorsMetrics?.data?.totalPayoutToAmbassadors ?? 0}
            text="Total payout to ambassadors"
            isCurrency={true}
            loading={fetchingAmbassadorsMetrics}
          />
          <CountCard
            count={ambassadorsMetrics?.data?.totalSignups ?? 0}
            text="Total signups"
            isCurrency={false}
            loading={fetchingAmbassadorsMetrics}
          />
          <CountCard
            count={ambassadorsMetrics?.data?.totalFirstPurchases ?? 0}
            text="Total first purchases"
            isCurrency={false}
            loading={fetchingAmbassadorsMetrics}
          />
          <CountCard
            count={ambassadorsMetrics?.data?.totalPurchases ?? 0}
            text="Total purchases"
            isCurrency={false}
            loading={fetchingAmbassadorsMetrics}
          />
        </CountCardContainer>

        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="!w-full md:!max-w-[400px]">
            <SearchInput
              searchFunction={(search) => {
                setSearchParams((prev) => {
                  const current = Object.fromEntries(prev)

                  return {
                    ...current,
                    search,
                    page: '1',
                  }
                })
              }}
              placeholder="Search"
              initialValue={searchParams.get('search') || undefined}
            />
          </div>

          <div className="flex items-center gap-2">
            <Button
              label={`Add ambassador`}
              primary={true}
              className="!font-normal ml-auto"
              onClick={() => {
                setSelectedAmbassadorId(undefined)
                handleModalOpen('addAmbassadorModal')
              }}
            />
          </div>
        </div>

        <div className="w-full overflow-x-scroll">
          <TableComponent
            name="Ambassadors"
            showName={false}
            headers={[
              'AMBASSADORS',
              'TOTAL REVENUE',
              'AMOUNT EARNED',
              'TOTAL SIGNUPS',
              'TOTAL FIRST PURCHASES',
              'ACTIONS',
            ]}
            rows={
              ambassadors?.data?.docs?.map((item) => {
                return {
                  id: item.ambassadorId,
                  content: [
                    <div
                      key={`${item.ambassadorId}-ambassador-nae`}
                      className="flex gap-2 items-center"
                    >
                      <Avatar name={item.ambassadorName} useRandomBgColor />

                      <span className="!capitalize">{item.ambassadorName}</span>
                    </div>,
                    formatCurrency(item.totalRevenueGenerated, {
                      currency: 'NGN',
                      decimalPlaces: 2,
                    }),
                    formatCurrency(item.ambassadorEarnings, {
                      currency: 'NGN',
                      decimalPlaces: 2,
                    }),
                    formatNumber(item.ambassadorSignups),
                    formatNumber(item.ambassadorFirstPurchases),
                    <Dropdown
                      key={`${item.ambassadorId}-controls`}
                      menuButton={
                        <Icon
                          id="ellipses"
                          height={18}
                          width={18}
                          className=""
                        />
                      }
                      menuItems={[
                        {
                          name: 'Edit',

                          value: '',
                          onClick: () => {
                            setSelectedAmbassadorId(item.ambassadorId)
                            handleModalOpen('editAmbassadorModal')
                          },
                        },
                        {
                          name: 'Delete ambassador',
                          value: '',
                          onClick: () => {
                            setSelectedAmbassadorId(item.ambassadorId)
                            handleModalOpen('deleteConfirmationModal')
                          },
                          className: '!text-danger',
                        },
                      ]}
                    />,
                  ],
                }
              }) ?? []
            }
            onPageChange={(page) => {
              setSearchParams((prev) => {
                const current = Object.fromEntries(prev)

                return {
                  ...current,
                  page: `${page}`,
                }
              })
            }}
            pageLimit={Number(query?.limit ?? 0)}
            currentPage={Number(query?.page ?? 0)}
            loading={fetchingAmbassadors}
            isEmpty={
              isError ||
              [undefined, 0].includes(ambassadors?.data?.docs?.length)
            }
            totalDataCount={ambassadors?.data.total ?? 0}
          />
        </div>
      </div>

      <AddAmbassadorModal
        open={
          (modal === 'addAmbassadorModal' || modal === 'editAmbassadorModal') &&
          modalOpen
        }
        setOpen={setModalOpen}
        onCloseCallback={() => {
          handleModalClose()
        }}
        onSuccessCallback={() => {
          handleModalClose()
          refetchAmbassadorsMetrics()
        }}
        ambassadorId={selectedAmbassadorId}
      />

      <ConfirmationModal
        open={modal === 'deleteConfirmationModal' && modalOpen}
        setOpen={setModalOpen}
        message="Are you sure you want to delete this ambassador customer?"
        title="Delete this ambassador customer"
        onCloseCallback={() => {
          setSelectedAmbassadorId(undefined)
          handleModalClose()
        }}
        onConfirm={async () => {
          const resp = await deleteAffiliate({
            id: selectedAmbassadorId!,
          })

          if (resp.data?.success) {
            toast.success(resp.data.message)
            handleModalClose()
          }
        }}
        loading={deletingAffiliate}
        confirmText="Delete"
      />
    </>
  )
}
